import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useEmployer } from "@/context/EmployerContext";
import AdminLogo from "@/components/AdminLogo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, LogIn } from "lucide-react";

export default function EmployerLogin() {
  const { t } = useTranslation();
  const { login } = useEmployer();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/catalog";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("emp.login_ok", "Welcome back"));
      navigate(next, { replace: true });
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        navigate("/employer/pending", { state: { email, message: err.response.data.detail } });
      } else {
        toast.error(err.response?.data?.detail || t("emp.login_failed", "Invalid credentials"));
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 grain relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-25 pointer-events-none"
           style={{ background: "radial-gradient(circle, #b8860b 0%, transparent 70%)" }} />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-20 pointer-events-none"
           style={{ background: "radial-gradient(circle, #d4a017 0%, transparent 70%)" }} />
      <div className="w-full max-w-md p-9 bg-zinc-900/85 backdrop-blur-2xl rounded-[28px] border border-zinc-800/80 shadow-2xl relative">
        <div className="flex items-center gap-3 mb-8">
          <AdminLogo size={44} variant="light" />
          <div className="text-[10px] tracking-[0.24em] uppercase text-zinc-500 font-medium">Employer Portal</div>
        </div>
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#d4a017] font-semibold mb-2">
          {t("emp.login_overline", "Employer Sign-In")}
        </div>
        <h1 className="font-serif text-white text-3xl mb-6 tracking-tight">
          {t("emp.login_title", "Welcome back")}
        </h1>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-wider font-medium">{t("emp.email", "Email")}</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-zinc-800/70 border-zinc-700 text-white focus:border-[#d4a017]" data-testid="emp-login-email" required />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-wider font-medium">{t("emp.password", "Password")}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-zinc-800/70 border-zinc-700 text-white focus:border-[#d4a017]" data-testid="emp-login-pw" required />
          </div>
          <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-60 flex items-center justify-center gap-2" data-testid="emp-login-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={14} />}
            {loading ? t("emp.signing_in", "Signing in…") : t("emp.sign_in", "Sign in")}
          </button>
        </form>
        <div className="mt-6 text-sm text-zinc-400 text-center">
          {t("emp.no_account", "No account yet?")}{" "}
          <Link to="/employer/register" className="text-[#d4a017] hover:underline" data-testid="emp-goto-register">
            {t("emp.register_now", "Create one")}
          </Link>
        </div>
      </div>
    </div>
  );
}
